import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { CartProvider } from "@/context/CartContext";
import { AdminProvider } from "@/context/AdminContext";
import { Toaster } from "@/components/ui/sonner";

import Home from "@/pages/Home";
import Cart from "@/pages/Cart";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import AdminLogin from "@/pages/AdminLogin";
import Admin from "@/pages/Admin";
import Maintenance from "@/pages/Maintenance";
import PreorderBanner from "@/components/PreorderBanner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Hard-coded fallback dates in case the API call fails (mirror backend defaults)
const PREORDER_OPEN_FALLBACK = new Date("2026-06-01T00:00:00Z");
const LAUNCH_FALLBACK = new Date("2026-07-01T00:00:00Z");

function computeLocalPhase() {
    const now = new Date();
    if (now < PREORDER_OPEN_FALLBACK) return "maintenance";
    if (now < LAUNCH_FALLBACK) return "preorder";
    return "live";
}

function App() {
    // Optimistic: use local date math instantly so first paint isn't blank
    const [status, setStatus] = useState({
        phase: computeLocalPhase(),
        preorder_opens: PREORDER_OPEN_FALLBACK.toISOString(),
        launch_date: LAUNCH_FALLBACK.toISOString(),
    });

    useEffect(() => {
        let canceled = false;
        axios
            .get(`${BACKEND}/api/shop-status`, { timeout: 8000 })
            .then((r) => {
                if (!canceled && r.data?.phase) setStatus(r.data);
            })
            .catch(() => {
                /* fall back to local date math */
            });
        return () => {
            canceled = true;
        };
    }, []);

    const inMaintenance = status.phase === "maintenance";
    const inPreorder = status.phase === "preorder";

    return (
        <div className="App">
            <CartProvider>
                <AdminProvider>
                    <BrowserRouter>
                        {inPreorder && (
                            <PreorderBanner launchDate={status.launch_date} />
                        )}
                        <Routes>
                            {inMaintenance ? (
                                <>
                                    {/* Admin remains reachable while shop is closed */}
                                    <Route
                                        path="/admin/login"
                                        element={<AdminLogin />}
                                    />
                                    <Route path="/admin" element={<Admin />} />
                                    {/* Everything else shows the splash */}
                                    <Route
                                        path="*"
                                        element={
                                            <Maintenance
                                                preorderOpens={
                                                    status.preorder_opens
                                                }
                                                launchDate={status.launch_date}
                                            />
                                        }
                                    />
                                </>
                            ) : (
                                <>
                                    <Route path="/" element={<Home />} />
                                    <Route path="/cart" element={<Cart />} />
                                    <Route
                                        path="/checkout/success"
                                        element={<CheckoutSuccess />}
                                    />
                                    <Route
                                        path="/admin/login"
                                        element={<AdminLogin />}
                                    />
                                    <Route path="/admin" element={<Admin />} />
                                    <Route path="*" element={<Home />} />
                                </>
                            )}
                        </Routes>
                    </BrowserRouter>
                </AdminProvider>
                <Toaster
                    theme="dark"
                    position="bottom-right"
                    toastOptions={{
                        style: {
                            background: "#151A22",
                            border: "1px solid #1E293B",
                            color: "#F8FAFC",
                            borderRadius: 0,
                            fontFamily: "IBM Plex Sans, sans-serif",
                        },
                    }}
                />
            </CartProvider>
        </div>
    );
}

export default App;
